import * as React from 'react';
import { Box, Button, Paper, Typography } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import CheckIcon from '@material-ui/icons/Check';
import { useTaskflowBuilder } from '../taskflowBuilderContext';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: theme.spacing(5, 3),
        },
        card: {
            width: '100%',
            maxWidth: 480,
            padding: theme.spacing(4),
            borderRadius: 10,
            textAlign: 'center',
        },
        icon: {
            width: 56,
            height: 56,
            borderRadius: '50%',
            backgroundColor: theme.palette.success.main,
            color: theme.palette.success.contrastText,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto',
            marginBottom: theme.spacing(2.25),
        },
        heading: {
            fontSize: 20,
            fontWeight: 700,
        },
        sub: {
            fontSize: 13.5,
            color: theme.palette.text.secondary,
            marginTop: theme.spacing(1),
            maxWidth: 480,
            marginInline: 'auto',
        },
        stats: {
            display: 'flex',
            justifyContent: 'center',
            gap: theme.spacing(4),
            marginTop: theme.spacing(3.5),
        },
        statValue: {
            fontSize: 20,
            fontWeight: 700,
        },
        statLabel: {
            fontSize: 11,
            color: theme.palette.text.disabled,
            fontWeight: 700,
            textTransform: 'uppercase',
        },
        actions: {
            marginTop: theme.spacing(4),
            display: 'flex',
            justifyContent: 'center',
            gap: theme.spacing(1.5),
        },
    })
);

export const PublishStep: React.FC = () => {
    const classes = useStyles();
    const { activeTaskflow, dispatch } = useTaskflowBuilder();

    if (!activeTaskflow) {
        return null;
    }

    const tasks = activeTaskflow.taskOrder.map(taskId => activeTaskflow.tasks[taskId]).filter(Boolean);
    const distinctResponsibilities = new Set(tasks.map(task => task.lane)).size;
    const gateCount = tasks.filter(task => task.isGate).length;

    const backToLibrary = () => dispatch({ type: 'SET_STEP', step: 'library' });
    const publish = () => {
        dispatch({ type: 'PUBLISH_ACTIVE_TASKFLOW' });
        dispatch({ type: 'SET_STEP', step: 'library' });
    };

    return (
        <Box className={classes.root}>
            <Paper variant="outlined" className={classes.card}>
                <Box className={classes.icon}>
                    <CheckIcon fontSize="large" />
                </Box>
                <Typography className={classes.heading}>Taskflow saved</Typography>
                <Typography className={classes.sub}>
                    {activeTaskflow.status === 'published'
                        ? `"${activeTaskflow.name || 'Untitled taskflow'}" is published — matters can now pick it up.`
                        : `"${activeTaskflow.name || 'Untitled taskflow'}" is saved as a draft. Publish it once you're ready for matters to pick it up.`}
                </Typography>

                <Box className={classes.stats}>
                    <Box>
                        <Typography className={classes.statValue}>{tasks.length}</Typography>
                        <Typography className={classes.statLabel}>Tasks</Typography>
                    </Box>
                    <Box>
                        <Typography className={classes.statValue}>{distinctResponsibilities}</Typography>
                        <Typography className={classes.statLabel}>Responsibilities</Typography>
                    </Box>
                    <Box>
                        <Typography className={classes.statValue}>{gateCount}</Typography>
                        <Typography className={classes.statLabel}>Decision points</Typography>
                    </Box>
                </Box>

                <Box className={classes.actions}>
                    <Button variant="outlined" onClick={backToLibrary}>Back to library</Button>
                    {activeTaskflow.status === 'draft' && (
                        <Button variant="contained" color="primary" onClick={publish}>Publish Taskflow</Button>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};
